/**
 * Changed layout of the whole form.
 * added new sx styles and components.
 */

import { PasswordInput } from '@/components/blocks/PasswordInput';
import { SessionErrorDialogSx } from '@/components/content/SessionErrorDialog/styles/DialogContentError';
import { UserLogon } from '@/data/Content/Login';
import { useLocalization } from '@/data/Localization';
import { ADDRESS_FIELD_LENGTH } from '@/data/constants/addressFields';
import { useForm } from '@/utils/useForm';
import {
	Box,
	Button,
	DialogActions,
	FormControlLabel,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { FC, FormEvent, SyntheticEvent, useMemo } from 'react';

const initSessionValues: UserLogon = {
	logonId: '',
	logonPassword: '',
	rememberMe: false,
};

type Props = {
	onSubmit: (login: UserLogon, event?: FormEvent) => void;
	handleCancel: (event: SyntheticEvent) => void;
	text?: string;
	logonId?: string;
};

export const SessionErrorDialogLoginForm: FC<Props> = ({
	onSubmit,
	handleCancel,
	text = '',
	logonId = '',
}) => {
	const registerLabels = useLocalization('RegistrationLayout');
	const initValues = useMemo(() => ({ ...initSessionValues, logonId }), [logonId]);
	const {
		handleInputChange,
		values: loginValues,
		formRef,
		error,
		handleSubmit,
	} = useForm(initValues);

	const SignInNLS = useLocalization('SignIn');
	const SessionErrorNLS = useLocalization('SessionError');
	return (
		<Stack
			onSubmit={handleSubmit(onSubmit)}
			name="sessionErrorSignInForm"
			noValidate
			ref={formRef}
			component="form"
		>
			<Typography variant="body1">{text}</Typography>
			<TextField
				variant="outlined"
				margin="normal"
				required
				name="logonId"
				autoComplete="username"
				label={SessionErrorNLS.Email.t()}
				value={!!logonId ? logonId : loginValues.logonId}
				onChange={handleInputChange}
				inputProps={{ maxLength: ADDRESS_FIELD_LENGTH.logonId }}
				error={error.logonId}
				placeholder={registerLabels.emailPlaceholder.t()}
			/>
			<PasswordInput
				required
				name="logonPassword"
				label={SignInNLS.Password.t()}
				value={loginValues.logonPassword}
				onChange={handleInputChange}
				error={error.logonPassword}
				inputProps={{
					maxLength: 100,
				}}
			/>
			<Box>
				<FormControlLabel
					control={
						<Switch
							name="rememberMe"
							checked={loginValues.rememberMe}
							onChange={handleInputChange}
							color="primary"
						/>
					}
					label={SignInNLS.Label.rememberMe.t()}
					sx={SessionErrorDialogSx.switchLabel}
				/>
				<Typography variant="bodySubText" sx={SessionErrorDialogSx.switchSubText}>
					{SignInNLS.RememberMeSwitchCopy.t()}
				</Typography>
			</Box>
			<DialogActions sx={SessionErrorDialogSx.dialogAction}>
				<Button
					data-testid="session-error-submit"
					id="session-error-submit"
					size="large"
					type="submit"
					variant="contained"
					sx={SessionErrorDialogSx.submitButton}
				>
					{SessionErrorNLS.SubmitButton.t()}
				</Button>
				<Button
					data-testid="session-error-cancel"
					id="session-error-cancel"
					size="large"
					onClick={handleCancel}
					variant="text"
				>
					{SessionErrorNLS.CancelButton.t()}
				</Button>
			</DialogActions>
		</Stack>
	);
};
